import { Pressable, ScrollView, Text, View } from 'react-native';
import { useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import MetaDataItem from '@/components/General/MetaDataItem';
import Chip from '@/components/General/Chip';
import Unicon from '@/components/General/Unicon';
import { formatCampaignDate, senderName, statusColorVariant } from './campaignHelpers';

/**
 * Shared detail sheet for campaign rows (SMS + Email).
 * Header card: icon, optional title, sender + timestamp, status chip, optional body text,
 * optional info chips (e.g. Type, Template).
 * Stats: square value cards. Extra key/value meta rows below.
 *
 * @param {object} props
 * @param {object|null} props.campaign - campaign row (null closes the sheet)
 * @param {() => void} props.onClose
 * @param {string} props.modalTitle - SheetModal title
 * @param {string} props.icon - Unicon name for the header card
 * @param {string} [props.title] - headline inside the header card (e.g. email subject)
 * @param {string} [props.body] - long text inside the header card (e.g. SMS messageBody)
 * @param {{label: string, value: string, colorVariant?: string}[]} [props.headerChips] - info chips below header top row
 * @param {{label: string, value: string|number, colorVariant?: string}[]} [props.stats] - square value cards
 * @param {'grid'|'3-then-square'} [props.statsLayout='grid'] - how to lay out stat cards.
 *        'grid': equal-flex cards that wrap. '3-then-square': first 3 in a row of
 *        equal-height cards, remaining cards each render full-width and square.
 * @param {React.ReactNode} [props.recipientsSection] - optional node rendered after meta (e.g. recipient list)
 * @param {{label: string, value: string}[]} [props.meta] - extra MetaDataItem rows
 * @param {string} props.testID
 */
const CampaignDetailSheet = ({ campaign, onClose, modalTitle, icon, title, body, headerChips = [], stats = [], statsLayout = 'grid', meta = [], recipientsSection, testID }) => {
    const [bodyExpanded, setBodyExpanded] = useState(false);
    const close = () => { setBodyExpanded(false); onClose(); };
    return (
        <SheetModal
            isOpen={!!campaign}
            onClose={close}
            title={modalTitle}
            scrollable={!recipientsSection}
            width={560}
        >
            {campaign && (
                <View testID={testID} style={styles.container}>
                    <View style={styles.headerCard}>
                        <View style={styles.headerTopRow}>
                            <View style={styles.iconWrap}>
                                <Unicon name={icon} size={22} colorVariant="connect" />
                            </View>
                            <View style={styles.headerTextWrap}>
                                {!!title && <Text style={styles.headerTitle} numberOfLines={2}>{title}</Text>}
                                <Text style={styles.senderLine} numberOfLines={1}>
                                    {senderName(campaign)} · {formatCampaignDate(campaign.createdAt)}
                                </Text>
                            </View>
                            {!!campaign.status && (
                                <Chip colorVariant={statusColorVariant(campaign.status)} style={styles.statusChip}>
                                    {campaign.status}
                                </Chip>
                            )}
                        </View>
                        {headerChips.length > 0 && (
                            <View style={styles.headerChipsRow}>
                                {headerChips.map(chip => (
                                    <Chip key={chip.label} colorVariant={chip.colorVariant || 'textSecondary'} icon={chip.icon}>
                                        {chip.value}
                                    </Chip>
                                ))}
                            </View>
                        )}
                        {!!body && <View style={styles.bodyWrap}>
                            <ScrollView style={styles.bodyScroll(bodyExpanded)} nestedScrollEnabled>
                                <Text selectable style={styles.bodyText} numberOfLines={bodyExpanded ? undefined : 2}>{body}</Text>
                            </ScrollView>
                            {body.length > 60 && <Pressable testID="campaign-body-toggle" onPress={() => setBodyExpanded(value => !value)}>
                                <Text style={styles.bodyToggle}>{bodyExpanded ? 'Show less' : 'Read full message'}</Text>
                            </Pressable>}
                        </View>}
                    </View>

                    {stats.length > 0 && statsLayout === '3-then-square' && (
                        <View style={styles.statsStack}>
                            <View style={styles.statsRow}>
                                {stats.slice(0, 3).map(stat => (
                                    <View key={stat.label} style={styles.statCard3}>
                                        <Text
                                            style={[
                                                styles.statValue,
                                                stat.colorVariant && styles.statValueColored(stat.colorVariant),
                                            ]}
                                            numberOfLines={1}
                                            adjustsFontSizeToFit
                                        >
                                            {stat.value}
                                        </Text>
                                        <Text style={styles.statLabel} numberOfLines={1}>{stat.label}</Text>
                                    </View>
                                ))}
                            </View>
                            {stats.slice(3).map(stat => (
                                <View key={stat.label} style={styles.statCardBig}>
                                    <Text
                                        style={[
                                            styles.statValueBig,
                                            stat.colorVariant && styles.statValueColored(stat.colorVariant),
                                        ]}
                                        numberOfLines={1}
                                        adjustsFontSizeToFit
                                    >
                                        {stat.value}
                                    </Text>
                                    <Text style={styles.statLabel} numberOfLines={1}>{stat.label}</Text>
                                </View>
                            ))}
                        </View>
                    )}

                    {stats.length > 0 && statsLayout !== '3-then-square' && (
                        <View style={styles.statsGrid}>
                            {stats.map(stat => (
                                <View key={stat.label} style={styles.statCard}>
                                    <Text
                                        style={[
                                            styles.statValue,
                                            stat.colorVariant && styles.statValueColored(stat.colorVariant),
                                        ]}
                                        numberOfLines={1}
                                        adjustsFontSizeToFit
                                    >
                                        {stat.value}
                                    </Text>
                                    <Text style={styles.statLabel} numberOfLines={1}>{stat.label}</Text>
                                </View>
                            ))}
                        </View>
                    )}

                    {meta.map(row => (
                        <MetaDataItem key={row.label} label={row.label} value={row.value} />
                    ))}

                    {recipientsSection}
                </View>
            )}
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: {
        flex: 1,
        minHeight: 0,
        gap: 12,
        paddingHorizontal: 4,
        paddingBottom: 8,
    },
    headerCard: {
        backgroundColor: colors.surface,
        borderRadius: 16,
        padding: 14,
        boxShadow: shadows.card,
    },
    headerTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    iconWrap: {
        width: 42,
        height: 42,
        borderRadius: 21,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.surfaceVariant,
    },
    headerTextWrap: {
        flex: 1,
        gap: 2,
    },
    headerTitle: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    senderLine: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    statusChip: {
        alignSelf: 'flex-start',
        textTransform: 'capitalize',
    },
    headerChipsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 10,
    },
    bodyWrap: {
        marginTop: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: colors.surfaceVariant,
        gap: 4,
    },
    bodyScroll: expanded => ({
        maxHeight: expanded ? 130 : 44,
    }),
    bodyToggle: {
        color: colors.connect,
        fontFamily: fonts.bold,
        fontSize: 11,
    },
    bodyText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
        lineHeight: 19,
    },
    statsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    statCard: {
        flexGrow: 1,
        flexBasis: 95,
        minHeight: 66,
        backgroundColor: colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: 8,
    },
    // '3-then-square' layout: row of 3 equal-height cards, then full-width square cards.
    statsStack: {
        gap: 10,
    },
    statsRow: {
        flexDirection: 'row',
        gap: 10,
    },
    statCard3: {
        flex: 1,
        minHeight: 96,
        backgroundColor: colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: 8,
    },
    statCardBig: {
        width: '100%',
        aspectRatio: 3.2,
        backgroundColor: colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: 12,
    },
    statValue: {
        fontSize: 19,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    statValueBig: {
        fontSize: 28,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    statValueColored: (colorVariant) => ({
        color: colors[colorVariant],
    }),
    statLabel: {
        fontSize: 11,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
}));

export default CampaignDetailSheet;
